import { createSlice } from "@reduxjs/toolkit";
import {
  ClubData,
  ContentLabels,
  CountryData,
  LeagueData,
  LiveEventOption,
} from "@repo/shared/api";
import { fetchBaseData } from "@repo/shared/redux";
import type { EventToken } from "@repo/shared/types";
import {
  ChemstyleBonuses,
  capitalizeWords,
  chemStyleCategories,
  chemStyles,
  inGameAttributesDict,
} from "@repo/shared/utils";

import { APIStatus } from "../../api/APIStatus.type";
import {
  Formations,
  FormationsAsList,
} from "../../api/privateRequests/squad-builder/get-formations";
import { ChemistryProfiles } from "../../functions/calculateChemistry";
import { defaultPositions } from "../players/playersSlice";
import { IRootState } from "../store";

type ColorInfo = {
  topText: string;
  bottomText: string;
  strip: string;
  lines: string;
};

export type VersionSubInfo = {
  colors: ColorInfo;
  url: string;
};

type SizeversionAssets = {
  [key: string]: VersionSubInfo;
};

type VersionEntry = {
  large: SizeversionAssets | VersionSubInfo;
  small: SizeversionAssets | VersionSubInfo;
  hasStrip: boolean;
  hasLevels: boolean;
  name: string;
  slug?: string;
  hidden: boolean;
  createdAt: string;
};

type VersionData = {
  id: number;
  name: string;
  hasLevels: boolean;
};

export type VersionAssets = {
  [key: string]: VersionEntry;
};

type ImageContent = string;

type LoadedContent = {
  images: ImageContent[];
  versionAssets: VersionAssets;
  countries?: CountryData[];
  leagues?: LeagueData[];
  clubs?: ClubData[];
  versions?: VersionData[];
  formations: Formations;
  versionsStatus: APIStatus;
  formationsStatus: APIStatus;
  leaguesCountriesClubsStatus: APIStatus;
  chemistryProfilesStatus: APIStatus;
  chemistryProfiles: ChemistryProfiles;
  chemstyleBonusesStatus: APIStatus;
  chemstyleBonuses: ChemstyleBonuses;
  eventTokensStatus: APIStatus;
  eventTokens: EventToken[];
  /** Localized display strings (see `ContentLabels`); English until fetched. */
  labels: ContentLabels;
  labelsStatus: APIStatus;
  /** Live events with squad rules, for the squad builder's live event filter. */
  liveEvents: LiveEventOption[];
};

// GK card face stats have no row in inGameAttributesDict; same keys as the
// backend `playerattributes` rows.
const GK_FACE_STAT_SHORTS = {
  diving: "DIV",
  handling: "HAN",
  kicking: "KIC",
  reflexes: "REF",
  speed: "SPD",
  positioningTitle: "POS",
};

const FOCUSES = [
  "Defend",
  "Balanced",
  "Attack",
  "Roaming",
  "Build-Up",
  "Complete",
  "Wide",
  "Aggressive",
  "Ball-Winning",
  "Support",
  "Versatile",
];

// The English dict, built from the static tables in the shape the API
// returns, so nothing renders blank before the fetch and a failed fetch
// leaves the app in English.
const englishContentLabels = (): ContentLabels => ({
  chemstyles: Object.fromEntries(
    Object.entries(chemStyles).map(([id, name]) => {
      const group = Object.keys(chemStyleCategories).find((g) =>
        (chemStyleCategories[g] as readonly number[]).includes(+id),
      );
      return [id, { name: capitalizeWords(name), group, groupName: group }];
    }),
  ),
  playerAttributes: {
    ...Object.fromEntries(
      // Only the all-caps face-stat titles ("PACE") differ from the DB form
      // ("Pace"); "FK Accuracy" must not become "Fk Accuracy".
      Object.entries(inGameAttributesDict).map(([key, { label, short }]) => [
        key,
        {
          label: label === label.toUpperCase() ? capitalizeWords(label) : label,
          short,
        },
      ]),
    ),
    ...Object.fromEntries(
      Object.entries(GK_FACE_STAT_SHORTS).map(([key, short]) => [
        key,
        { label: capitalizeWords(key.replace("Title", "")), short },
      ]),
    ),
  },
  positions: Object.fromEntries(
    defaultPositions.map(({ label, category }) => [
      label,
      { name: label, zone: category, zoneName: category },
    ]),
  ),
  focuses: Object.fromEntries(FOCUSES.map((name) => [name, { name }])),
});

const initialState: LoadedContent = {
  images: [],
  versionAssets: {},
  countries: [],
  leagues: [],
  clubs: [],
  versions: [],
  formations: {},
  versionsStatus: "idle",
  formationsStatus: "idle",
  leaguesCountriesClubsStatus: "idle",
  chemistryProfilesStatus: "idle",
  chemistryProfiles: {} as ChemistryProfiles,
  chemstyleBonusesStatus: "idle",
  chemstyleBonuses: {} as ChemstyleBonuses,
  eventTokensStatus: "idle",
  eventTokens: [],
  labels: englishContentLabels(),
  labelsStatus: "idle",
  liveEvents: [],
};

const PREMIER_LEAUGE_ID = 13;
const LA_LIGA_ID = 53;
const BUNDESLIGA_ID = 19;
const LIGUE_1_ID = 16;
const SERIE_A_ID = 31;

const ARGENTINA_ID = 52;
const BRAZIL_ID = 54;
const ENGLAND_ID = 14;
const FRANCE_ID = 18;
const GERMANY_ID = 21;
const NETHERLANDS_ID = 34;
const ITALY_ID = 27;
const PORTUGAL_ID = 38;
const SPAIN_ID = 45;

export const TOP_FIVE_LEAGUE_IDS = [
  PREMIER_LEAUGE_ID,
  LA_LIGA_ID,
  BUNDESLIGA_ID,
  LIGUE_1_ID,
  SERIE_A_ID,
];

export const TOP_NINE_COUNTRY_IDS = [
  ENGLAND_ID,
  SPAIN_ID,
  GERMANY_ID,
  FRANCE_ID,
  ITALY_ID,
  PORTUGAL_ID,
  NETHERLANDS_ID,
  BRAZIL_ID,
  ARGENTINA_ID,
];

const LoadedContentSlice = createSlice({
  name: "loadedContent",
  initialState,
  reducers: {
    setImageLoaded(state, action) {
      if (!state.images.find((imageLoaded) => action.payload === imageLoaded)) {
        state.images = [...state.images, action.payload];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBaseData.pending, (state) => {
        state.versionsStatus = "pending";
        state.leaguesCountriesClubsStatus = "pending";
        state.formationsStatus = "pending";
        state.chemistryProfilesStatus = "pending";
        state.chemstyleBonusesStatus = "pending";
        state.eventTokensStatus = "pending";
        state.labelsStatus = "pending";
      })
      .addCase(fetchBaseData.fulfilled, (state, action) => {
        const { versionAssets, managerData, labels } = action.payload;
        state.versionAssets = versionAssets;
        state.versions = Object.keys(versionAssets)
          .filter((key) => !versionAssets[key].hidden)
          .map((key) => ({
            id: parseInt(key),
            name: versionAssets[key].name,
            hasLevels: versionAssets[key].hasLevels,
          }));
        state.versionsStatus = "fulfilled";
        state.countries = managerData.countries;
        state.leagues = managerData.leagues;
        state.clubs = managerData.clubs;
        state.leaguesCountriesClubsStatus = "fulfilled";
        state.formations = Object.fromEntries(
          action.payload.formations.map(({ id, ...formation }) => [
            id,
            formation,
          ]),
        );
        state.formationsStatus = "fulfilled";
        state.chemistryProfiles = action.payload.chemistryProfiles;
        state.chemistryProfilesStatus = "fulfilled";
        state.chemstyleBonuses = action.payload.chemstyleBonuses;
        state.chemstyleBonusesStatus = "fulfilled";
        state.eventTokens = action.payload.eventTokens;
        state.eventTokensStatus = "fulfilled";
        // Merge per key over the English defaults: a key the backend has not
        // been seeded with yet keeps its English label.
        const fetchedLabels: ContentLabels = {
          chemstyles: labels.chemstyles,
          playerAttributes: labels.attributes,
          positions: labels.positions,
          focuses: labels.focuses,
        };
        for (const ns of Object.keys(state.labels) as (keyof ContentLabels)[]) {
          (state.labels as Record<string, unknown>)[ns] = {
            ...state.labels[ns],
            ...fetchedLabels[ns],
          };
        }
        state.labelsStatus = "fulfilled";
        state.liveEvents = action.payload.liveEvents;
      })
      .addCase(fetchBaseData.rejected, (state) => {
        state.versionsStatus = "rejected";
        state.leaguesCountriesClubsStatus = "rejected";
        state.formationsStatus = "rejected";
        state.chemistryProfilesStatus = "rejected";
        state.chemstyleBonusesStatus = "rejected";
        state.eventTokensStatus = "rejected";
        state.labelsStatus = "rejected";
      });
  },
});

export const getCountriesSelector = (country: number) => (state: IRootState) =>
  state.loadedContentReducer.countries?.find((c) => c.id === country);

export const getLeaguesSelector = (league: number) => (state: IRootState) =>
  state.loadedContentReducer.leagues?.find((l) => l.id === league);

export const getClubsSelector = (club: number) => (state: IRootState) =>
  state.loadedContentReducer.clubs?.find((c) => c.id === club);

export const getVersionAssetsSelector = (state: IRootState) =>
  state.loadedContentReducer.versionAssets;
export const chemstyleBonusesSelector = (state: IRootState) =>
  state.loadedContentReducer.chemstyleBonuses;
export const eventTokensSelector = (state: IRootState) =>
  state.loadedContentReducer.eventTokens;
export const contentLabelsSelector = (state: IRootState) =>
  state.loadedContentReducer.labels;
export const liveEventsSelector = (state: IRootState) =>
  state.loadedContentReducer.liveEvents;
export const getLoadedImages = (state: IRootState) =>
  state.loadedContentReducer.images;

export const loadedContentSelector = (state: IRootState) =>
  state.loadedContentReducer;

export const formationsSelector = (state: IRootState) => {
  return state.loadedContentReducer.formations;
};

export const getClubSelector = (clubId: number) => (state: IRootState) =>
  state.loadedContentReducer.clubs?.find((c) => c.id === clubId);

export const formationsAsListSelector = (
  state: IRootState,
): FormationsAsList => {
  return Object.entries(state.loadedContentReducer.formations).map(
    ([formationId, formation]) => ({
      id: formationId,
      ...formation,
    }),
  );
};

export const getFormationsLabel =
  (formationId: string) => (state: IRootState) =>
    state.loadedContentReducer.formations[formationId]?.label;

export const getFormationData =
  (formationId: string) => (state: IRootState) => {
    const formation = state.loadedContentReducer.formations[formationId];
    if (!formation) return null;
    return {
      labels: formation.positions.slice(0, 11),
      uniquePositions: formation.uniquePositions.slice(0, 11),
      coordinates: formation.pitchCoordinates.map((coord) => ({
        x: coord.x,
        y: coord.y,
      })),
    };
  };

export const { setImageLoaded } = LoadedContentSlice.actions;
export default LoadedContentSlice.reducer;
